import { ArticleDatabaseCatalog } from '../articles/article_database_catalog.js?v=20260831f';
import { collectScopes } from '../bom/bom_service.js?v=20260904c';

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function normalizeText(value) {
  return String(value ?? '').trim();
}

function finitePositiveNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
}

function finiteNonNegativeNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
}

function manualBomRows(project = {}) {
  const overrides = plainObject(project?.bomOverrides);
  return (Array.isArray(overrides.manualItems) ? overrides.manualItems : [])
    .filter(item => item && typeof item === 'object')
    .map(item => ({
      ...item,
      sourceSystemId: 'manual',
      isManual: true
    }));
}

async function collectPublicBomRows(options = {}) {
  if (Array.isArray(options.rows)) return options.rows;
  const bomModule = options.bomModule || await import('../bom/bom.js?v=20260903c');
  const scopes = Array.isArray(options.scopes)
    ? options.scopes
    : (options.collectScopesFn || collectScopes)();
  const scopeIds = scopes.map(scope => normalizeText(scope?.id)).filter(Boolean);
  const rawRows = bomModule.createBomRowsForScopeIds(scopeIds);
  const rootArticleNos = [...new Set(
    rawRows.map(row => rawArticleNumber(row, options.articleCatalog)).filter(Boolean)
  )];
  if (rootArticleNos.length && typeof options.articleCatalog?.ensureArticles === 'function') {
    try {
      await options.articleCatalog.ensureArticles(rootArticleNos);
    } catch (error) {
      options.logger?.warn?.('BOM-Wurzelartikel konnten vor der Baugruppenauflösung nicht vollständig geladen werden.', error);
      // Ohne den Wurzelkatalog kann eine offene Baugruppe nicht sicher vom
      // Beschaffungsartikel unterschieden werden. Ein Parent-Snapshot wäre
      // dann fachlich falsch und darf nicht als vollständig gespeichert
      // werden.
      throw new Error('BOM-Wurzelartikel konnten vor der Baugruppenauflösung nicht vollständig geladen werden.', {
        cause: error
      });
    }
  }
  const rawRowsBySystem = new Map();
  rawRows.forEach(row => {
    const sourceSystemId = normalizeText(row?.sourceSystemId);
    if (!rawRowsBySystem.has(sourceSystemId)) rawRowsBySystem.set(sourceSystemId, []);
    rawRowsBySystem.get(sourceSystemId).push(row);
  });
  const rootRows = [...rawRowsBySystem.entries()].flatMap(([sourceSystemId, systemRows]) => (
    bomModule.aggregateBomRows(systemRows, { groupMode: 'none' }).map(row => ({
      ...row,
      sourceSystemId: row.sourceSystemId || sourceSystemId
    }))
  ));
  return rootRows.flatMap(row => {
    if (!bomModule.isBomOpenAssembly(row, '', null, { isRootArticle: true, assemblyDepth: 0 })) {
      return row;
    }
    return bomModule.bomAssemblyItems(row, '', {
      isRootArticle: true,
      assemblyDepth: 0,
      parentQuantity: row.quantity,
      effectiveQuantities: true
    }).map(child => ({
      ...child,
      sourceSystemId: child.sourceSystemId || row.sourceSystemId || ''
    }));
  });
}

function rawArticleNumber(row, articleCatalog) {
  const value = row?.articleNo || row?.ART_NRA || row?.number || row?.articleNumber || '';
  if (typeof articleCatalog?.normalizeArticleNo === 'function') {
    return normalizeText(articleCatalog.normalizeArticleNo(value));
  }
  return normalizeText(value).toLowerCase();
}

function rowLabel(row = {}, article = null, articleCatalog = null) {
  const fromCatalog = [
    articleCatalog?.getArticleDescription1?.(article),
    articleCatalog?.getArticleSearchTerm?.(article)
  ].map(normalizeText).find(Boolean);
  return fromCatalog || [
    row.label,
    row.beschreibung1,
    row.description1,
    row.suchbegriff,
    row.searchTerm,
    row.name
  ].map(normalizeText).find(Boolean) || rawArticleNumber(row, articleCatalog);
}

function rowUnit(row = {}, article = null, articleCatalog = null) {
  return normalizeText(
    row.unit
      || row.einheit
      || articleCatalog?.getArticleUnit?.(article)
      || 'Stk'
  ) || 'Stk';
}

function rowUnitCostChf(row = {}, article = null, articleCatalog = null) {
  const rowValue = finiteNonNegativeNumber(
    row.unitCostChf
      ?? row.priceChf
      ?? row.einstandspreisChf
      ?? row.einstandspreis
  );
  if (rowValue !== null) return rowValue;
  if (!article) return null;
  return finiteNonNegativeNumber(articleCatalog?.getArticlePriceChf?.(article));
}

function normalizeUsageRow(row = {}, articleCatalog = null) {
  const articleNo = rawArticleNumber(row, articleCatalog);
  const quantity = finitePositiveNumber(row.quantity ?? row.menge ?? row.qty);
  if (!articleNo || quantity === null) return null;
  const article = articleCatalog?.get?.(articleNo)
    || articleCatalog?.getArticleByNo?.(articleNo)
    || null;
  const unitCostChf = rowUnitCostChf(row, article, articleCatalog);
  const sourceSystemCode = normalizeText(row.sourceSystemId || row.sourceSystemCode)
    || (row.isManual === true ? 'manual' : '');
  return {
    sourceSystemCode,
    articleNo,
    label: rowLabel(row, article, articleCatalog),
    unit: rowUnit(row, article, articleCatalog),
    quantity,
    unitCostChf,
    lineCostChf: unitCostChf === null ? null : quantity * unitCostChf,
    isManual: row.isManual === true || row.source === 'manual'
  };
}

function aggregateUsageRows(rows = []) {
  const byKey = new Map();
  for (const row of rows) {
    if (!row) continue;
    const key = [
      row.sourceSystemCode,
      row.articleNo,
      row.unit,
      row.unitCostChf ?? '',
      row.isManual ? 'manual' : 'drawing'
    ].join('|');
    const existing = byKey.get(key);
    if (existing) {
      existing.quantity += row.quantity;
      existing.lineCostChf = existing.unitCostChf === null
        ? null
        : existing.quantity * existing.unitCostChf;
      if (!existing.label && row.label) existing.label = row.label;
      continue;
    }
    byKey.set(key, { ...row });
  }
  return [...byKey.values()].sort((left, right) => (
    left.articleNo.localeCompare(right.articleNo)
    || left.sourceSystemCode.localeCompare(right.sourceSystemCode)
    || left.unit.localeCompare(right.unit)
  ));
}

/**
 * Creates the article-usage part of a statistics snapshot from the public BOM
 * hooks and the shared BOM view semantics. Open root assemblies are represented
 * by their effective child quantities instead of also counting the parent; the
 * project's manually added rows are appended explicitly.
 */
async function collectBomUsageSnapshot(options = {}) {
  const project = plainObject(options.project);
  const articleCatalog = options.articleCatalog || ArticleDatabaseCatalog;
  const logger = options.logger || console;
  const rawRows = [
    ...await collectPublicBomRows({ ...options, articleCatalog, logger }),
    ...manualBomRows(project)
  ];
  const articleNos = [...new Set(
    rawRows.map(row => rawArticleNumber(row, articleCatalog)).filter(Boolean)
  )];

  if (articleNos.length && typeof articleCatalog?.ensureArticles === 'function') {
    try {
      await articleCatalog.ensureArticles(articleNos);
    } catch (error) {
      logger?.warn?.('Artikelstamm konnte fuer den Statistik-Snapshot nicht vollstaendig geladen werden.', error);
    }
  }

  return aggregateUsageRows(
    rawRows.map(row => normalizeUsageRow(row, articleCatalog)).filter(Boolean)
  );
}

export {
  aggregateUsageRows,
  collectBomUsageSnapshot,
  manualBomRows,
  normalizeUsageRow
};
